import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Globe2, Loader2, Plus, Trash2, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin — region-level default rate cards (per role), shared across entities. */
export const RegionalRateCardsCard = () => {
  const [cards, setCards] = useState(null);
  const [roles, setRoles] = useState([]);
  const [baseCard, setBaseCard] = useState({});
  const [newRegion, setNewRegion] = useState('');
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/regional-rate-cards/history`, { withCredentials: true });
      setHistory(res.data.entries || []);
    } catch { setHistory([]); }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/regional-rate-cards`, { withCredentials: true });
      setCards(res.data.cards || {});
      setRoles(res.data.roles || []);
      setBaseCard(res.data.base_rate_card || {});
    } catch { /* stays loading */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRate = (region, role, value) => {
    setCards(prev => ({ ...prev, [region]: { ...prev[region], [role]: value } }));
  };

  const addRegion = () => {
    const key = newRegion.trim().toUpperCase();
    if (!key) return;
    if (cards[key]) { toast.error(`${key} already exists`); return; }
    setCards(prev => ({ ...prev, [key]: {} }));
    setNewRegion('');
  };

  const removeRegion = (region) => {
    setCards(prev => {
      const next = { ...prev };
      delete next[region];
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const clean = {};
      Object.entries(cards).forEach(([region, rc]) => {
        const rates = {};
        Object.entries(rc || {}).forEach(([role, v]) => {
          const n = parseFloat(v);
          if (Number.isFinite(n) && n > 0) rates[role] = n;
        });
        if (Object.keys(rates).length) clean[region] = rates;
      });
      const res = await axios.put(`${API_URL}/api/settings/regional-rate-cards`, { cards: clean }, { withCredentials: true });
      toast.success(res.data?.message || 'Regional rate cards saved');
      setCards(res.data.cards || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save regional rate cards');
    } finally { setBusy(false); }
  };

  const regions = cards ? Object.keys(cards).sort() : [];

  return (
    <Card className="border-slate-200" data-testid="regional-rate-cards-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-[#4ABFCF]" />
            <h3 className="text-sm font-semibold text-[#0F2040]">Regional Rate Cards</h3>
          </div>
          <Button size="sm" variant="ghost" onClick={openHistory} className="text-slate-500" data-testid="regional-history-btn">
            <History className="w-3 h-3 mr-1" /> History
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Default hourly rates per region (in the local currency), shared by every entity in that region.
          Map an entity to a region on the Entities tab — its estimates then use these defaults, still
          overrideable per estimate. Blank = global default rate.
        </p>
        {!cards ? (
          <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            {regions.length === 0 && (
              <p className="text-xs text-slate-400 italic" data-testid="regional-rate-cards-empty">
                No regional cards yet — add a region below (e.g. APAC, EMEA, AMER).
              </p>
            )}
            {regions.map(region => (
              <div key={region} className="border border-slate-200 rounded-lg p-3" data-testid={`regional-card-${region.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#0F2040] tracking-wider">{region}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeRegion(region)}
                    className="h-6 text-slate-400 hover:text-rose-600" data-testid={`regional-card-remove-${region.toLowerCase()}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
                  {roles.map(role => (
                    <div key={role}>
                      <div className="text-[10px] font-semibold text-slate-500">{role}
                        <span className="ml-1 font-normal text-slate-400">def {Number(baseCard[role] || 0).toFixed(0)}</span>
                      </div>
                      <Input value={cards[region]?.[role] ?? ''} placeholder={Number(baseCard[role] || 0).toFixed(0)}
                        onChange={e => setRate(region, role, e.target.value)}
                        className="h-7 mt-0.5 text-xs font-mono"
                        data-testid={`regional-rate-${region.toLowerCase()}-${role.toLowerCase()}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input value={newRegion} onChange={e => setNewRegion(e.target.value.toUpperCase())}
                placeholder="New region (e.g. APAC)" className="h-7 text-xs font-mono w-44"
                onKeyDown={e => { if (e.key === 'Enter') addRegion(); }}
                data-testid="regional-card-new-region-input" />
              <Button size="sm" variant="outline" onClick={addRegion} className="h-7"
                data-testid="regional-card-add-btn">
                <Plus className="w-3 h-3 mr-1" /> Add region
              </Button>
              <div className="flex-1" />
              <Button onClick={save} disabled={busy} size="sm"
                className="bg-[#0F2040] hover:bg-[#1a3156] text-white" data-testid="regional-rate-cards-save-btn">
                {busy ? 'Saving…' : 'Save Rate Cards'}
              </Button>
            </div>
          </div>
        )}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-2xl bg-white max-h-[75vh] overflow-y-auto" data-testid="regional-history-dialog">
            <DialogHeader><DialogTitle className="text-[#0F2040] text-base">Rate card change history</DialogTitle></DialogHeader>
            {!history ? (
              <div className="flex items-center gap-2 text-xs text-slate-400 py-6"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : history.length === 0 ? (
              <p className="text-xs text-slate-400 py-4">No changes recorded yet. Every save of the regional or internal-cost rate cards is logged here with a timestamp, so older estimates' pricing basis stays auditable (each estimate also snapshots the exact rates it was priced on in its saved results).</p>
            ) : (
              <div className="space-y-2">
                {history.map((h, i) => {
                  const regions = [...new Set([...Object.keys(h.before || {}), ...Object.keys(h.after || {})])];
                  return (
                    <div key={i} className="border border-slate-100 rounded-md p-2" data-testid={`regional-history-entry-${i}`}>
                      <p className="text-[11px] text-slate-600">
                        <span className="font-mono text-slate-400">{(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                        {' '}— <b>{h.kind === 'cost' ? 'Internal cost rates' : 'Regional rate cards'}</b> updated by {h.updated_by || 'admin'}
                      </p>
                      {h.kind !== 'cost' && regions.map(rg => {
                        const b = (h.before || {})[rg] || {}; const a = (h.after || {})[rg] || {};
                        const changed = [...new Set([...Object.keys(b), ...Object.keys(a)])]
                          .filter(role => b[role] !== a[role])
                          .map(role => `${role} ${b[role] ?? '—'}→${a[role] ?? '—'}`);
                        return changed.length ? (
                          <p key={rg} className="text-[10px] text-slate-500 font-mono ml-1">{rg}: {changed.join(', ')}</p>
                        ) : null;
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default RegionalRateCardsCard;
